/**
 * Decoder for the position buffer the API serves per round.
 *
 * Layout (little-endian), written by match/tracks.go:
 *
 *   0   magic "VTRK"
 *   4   version u8 | pawns u8 | reserved u16
 *   8   frames u32
 *   12  t0Ms u32
 *   16  stepUs u32
 *   20  pawn NetGUIDs, u32 each
 *       X float32[pawns*frames], then Y, then Z, then yaw u8[], then flags u8[]
 *
 * The float sections are four-byte aligned on purpose, so they become typed
 * array views over the same memory instead of copies.
 */

export const FLAG_PRESENT = 1;
export const FLAG_REAL = 2;

export interface Tracks {
  version: number;
  pawns: number[];
  frames: number;
  t0Ms: number;
  stepUs: number;
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  yaw: Uint8Array;
  flags: Uint8Array;
}

/** One player's interpolated state at a point in time. */
export interface Sample {
  present: boolean;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export function decodeTracks(buffer: ArrayBuffer): Tracks {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3),
  );
  if (magic !== "VTRK") throw new Error(`format de positions inattendu: ${magic}`);

  const version = view.getUint8(4);
  const pawnCount = view.getUint8(5);
  const frames = view.getUint32(8, true);
  const t0Ms = view.getUint32(12, true);
  const stepUs = view.getUint32(16, true);

  const pawns: number[] = [];
  for (let i = 0; i < pawnCount; i++) {
    pawns.push(view.getUint32(20 + 4 * i, true));
  }

  const base = 20 + 4 * pawnCount;
  const n = pawnCount * frames;
  return {
    version,
    pawns,
    frames,
    t0Ms,
    stepUs,
    x: new Float32Array(buffer, base, n),
    y: new Float32Array(buffer, base + 4 * n, n),
    z: new Float32Array(buffer, base + 8 * n, n),
    yaw: new Uint8Array(buffer, base + 12 * n, n),
    flags: new Uint8Array(buffer, base + 13 * n, n),
  };
}

/** Fractional frame index for a time, so playback can interpolate between
 *  grid points instead of stepping. */
export function frameAt(tracks: Tracks, timeMs: number): number {
  return ((timeMs - tracks.t0Ms) * 1000) / tracks.stepUs;
}

export function timeAtFrame(tracks: Tracks, frame: number): number {
  return tracks.t0Ms + (frame * tracks.stepUs) / 1000;
}

const EMPTY: Sample = { present: false, x: 0, y: 0, z: 0, yaw: 0 };

/** Reads one pawn's position at a time, interpolating between grid frames. */
export function sampleAt(tracks: Tracks, pawnIndex: number, timeMs: number): Sample {
  const exact = frameAt(tracks, timeMs);
  const frame = Math.floor(exact);
  if (frame < 0 || frame >= tracks.frames) return EMPTY;

  const base = pawnIndex * tracks.frames;
  const i = base + frame;
  if ((tracks.flags[i]! & FLAG_PRESENT) === 0) return EMPTY;

  const next = frame + 1;
  const hasNext =
    next < tracks.frames && (tracks.flags[base + next]! & FLAG_PRESENT) !== 0;
  const t = hasNext ? exact - frame : 0;
  const j = hasNext ? base + next : i;

  return {
    present: true,
    x: lerp(tracks.x[i]!, tracks.x[j]!, t),
    y: lerp(tracks.y[i]!, tracks.y[j]!, t),
    z: lerp(tracks.z[i]!, tracks.z[j]!, t),
    yaw: lerpAngle((tracks.yaw[i]! * 360) / 256, (tracks.yaw[j]! * 360) / 256, t),
  };
}

/** Positions of one pawn over a window ending at timeMs, for motion trails. */
export function trail(
  tracks: Tracks,
  pawnIndex: number,
  timeMs: number,
  windowMs: number,
): Array<[number, number]> {
  const points: Array<[number, number]> = [];
  const last = Math.floor(frameAt(tracks, timeMs));
  const first = Math.max(0, Math.floor(frameAt(tracks, timeMs - windowMs)));
  const base = pawnIndex * tracks.frames;
  for (let frame = first; frame <= last && frame < tracks.frames; frame++) {
    const i = base + frame;
    if ((tracks.flags[i]! & FLAG_PRESENT) === 0) {
      points.length = 0; // a gap means a death: start the trail again
      continue;
    }
    points.push([tracks.x[i]!, tracks.y[i]!]);
  }
  return points;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpAngle(a: number, b: number, t: number): number {
  let d = ((b - a) % 360 + 360) % 360;
  if (d > 180) d -= 360;
  return a + d * t;
}
